import { FC } from "react";
import moment from "moment";
import { Autocomplete, createFilterOptions, Box, Chip, IconButton, InputAdornment, TextField, Tooltip, Typography } from "@mui/material";
import { WhatsApp } from "@mui/icons-material";
import { Patient } from "../../../../../services/patient.service";
import { whatsappUrl } from "../../patient-format";

type Props = {
  formData: Patient;
  allTags: string[];
  allPatients?: Patient[];
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onTagsChange: (tags: string[]) => void;
  onSelectExisting?: (patient: Patient) => void;
};

const asDate = (value?: string) => (value ? moment(value).format("YYYY-MM-DD") : "");

const nameFilter = createFilterOptions<Patient>();

/** Aba "Informações" do modal de paciente. */
const PatientInfoTab: FC<Props> = ({ formData, allTags, allPatients, onChange, onTagsChange, onSelectExisting }) => {
  const waUrl = whatsappUrl(formData.phone);
  // Só sugere pacientes existentes ao criar um novo, pra reaproveitar em vez de duplicar.
  const showNameLookup = !formData.id && !!onSelectExisting && !!allPatients;

  return (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
    {showNameLookup ? (
      <Autocomplete
        freeSolo
        autoSelect
        options={allPatients!}
        filterOptions={(options, params) => nameFilter(options, params)}
        getOptionLabel={(option) => (typeof option === "string" ? option : option.name)}
        inputValue={formData.name}
        onInputChange={(_, value, reason) => {
          if (reason === "input" || reason === "clear") onChange({ target: { name: "name", value } } as React.ChangeEvent<HTMLInputElement>);
        }}
        onChange={(_, value) => {
          if (value && typeof value === "object") onSelectExisting!(value);
        }}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            {option.name}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            autoFocus
            margin="dense"
            label="Nome"
            fullWidth
            variant="outlined"
            required
            error={formData.name.length > 0 && formData.name.trim().length < 4}
            helperText={formData.name.length > 0 && formData.name.trim().length < 4 ? "O nome deve ter pelo menos 4 caracteres" : undefined}
          />
        )}
      />
    ) : (
      <TextField
        autoFocus
        margin="dense"
        name="name"
        label="Nome"
        fullWidth
        variant="outlined"
        value={formData.name}
        onChange={onChange}
        required
        error={formData.name.length > 0 && formData.name.trim().length < 4}
        helperText={formData.name.length > 0 && formData.name.trim().length < 4 ? "O nome deve ter pelo menos 4 caracteres" : undefined}
      />
    )}
    <TextField
      margin="dense"
      name="phone"
      label="Telefone"
      fullWidth
      variant="outlined"
      value={formData.phone || ""}
      onChange={onChange}
      placeholder="(11) 99999-9999"
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <Tooltip title={waUrl ? "Abrir conversa no WhatsApp" : "Informe um telefone"} placement="top">
              <span>
                <IconButton
                  aria-label="Abrir conversa no WhatsApp"
                  edge="end"
                  disabled={!waUrl}
                  onClick={() => window.open(waUrl, "_blank", "noopener,noreferrer")}
                  sx={{ color: waUrl ? "#25D366" : "text.disabled" }}
                >
                  <WhatsApp />
                </IconButton>
              </span>
            </Tooltip>
          </InputAdornment>
        ),
      }}
    />

    <Box>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        Tags
      </Typography>
      <Autocomplete
        multiple
        freeSolo
        options={allTags}
        value={formData.tags || []}
        onChange={(_, newValue) => onTagsChange(newValue)}
        renderTags={(value: string[], getTagProps) =>
          value.map((option: string, index: number) => <Chip variant="outlined" label={option} {...getTagProps({ index })} key={option} />)
        }
        renderInput={(params) => <TextField {...params} variant="outlined" label="Tags (ex: vip, retorno)" placeholder="Digite e pressione Enter" />}
      />
    </Box>

    <TextField
      margin="dense"
      name="returnDate"
      label="Data do Retorno"
      type="date"
      fullWidth
      variant="outlined"
      value={asDate(formData.returnDate)}
      onChange={onChange}
      InputLabelProps={{ shrink: true }}
    />
    <TextField margin="dense" name="observation" label="Observações" fullWidth variant="outlined" multiline rows={3} value={formData.observation || ""} onChange={onChange} />
    <TextField
      margin="dense"
      name="anamnese"
      label="Anamnese"
      fullWidth
      variant="outlined"
      multiline
      rows={4}
      value={formData.anamnese || ""}
      onChange={onChange}
      placeholder="Descreva os problemas do paciente, histórico médico, alergias, etc..."
    />
  </Box>
  );
};

export default PatientInfoTab;
